import { type ODataQuery, buildIdInFilter, buildIdNotInFilter, stringifyODataQuery } from "@sg-protect/core";
import type { ODataSelection } from "../types.ts";

/**
 * This is a cheap implement of equality check for ODataSelection objects.
 * It works by comparing the JSON stringified versions of the objects.
 * This is really to ensure that a when we recreate the same selection object, we know it's the same selection.
 * It wouldn't work if the order of the includes/excludes arrays were different, but in our usage scenarios, that shouldn't happen.
 */
export function areODataSelectionEqual(query1: ODataSelection, query2: ODataSelection): boolean {
    return JSON.stringify(query1) === JSON.stringify(query2);
}

// TODO: all those SingleAction  should be renamed to something other than action. IsSingleItemSelection? IsSingleEntitySelection?
function isSingleActionODataSelection(query: ODataSelection): boolean {
    return query.includes?.length === 1;
}

/**
 * Returns the single action ID from the ODataSelection if it contains exactly one included action.
 */
export function getSingleActionId(query: ODataSelection | null | undefined) {
    if (query && isSingleActionODataSelection(query)) {
        return query.includes![0];
    }

    return undefined;
}

export function createSingleActionSelection(entity: string, id: string): ODataSelection {
    return {
        entity,
        includes: [id],
        query: {}
    };
}

export function areSelectionQueriesEqual(a: ODataSelection, b: ODataSelection): boolean {
    return SELECTION_QUERY_KEYS.every(key => a.query[key] === b.query[key]);
}

export function isIncludedInSelection(currentBulkAction: ODataSelection, id: string): boolean {
    if (currentBulkAction.excludes && currentBulkAction.excludes.length > 0) {
        return !currentBulkAction.excludes.includes(id);
    }

    if (currentBulkAction.includes && currentBulkAction.includes.length > 0) {
        return currentBulkAction.includes.includes(id);
    }

    return true;
}

interface CreateQueryFromSelectionOptions {
    applySelection?: boolean;
    extraQuery?: Partial<ODataQuery>;
}

interface ResolveSelectionQueryOptions {
    applySelection?: boolean;
}

export const SELECTION_QUERY_KEYS = ["$filter", "$search"] satisfies (keyof ODataQuery)[];

// Entity sets keyed by Edm.String: their ids must be quoted in $filter, unlike Edm.Guid and numeric keys.
const STRING_ID_ENTITY_SETS: readonly string[] = ["groupPrincipals"];

export function hasStringIds(entity: string) {
    return STRING_ID_ENTITY_SETS.includes(entity);
}

export function resolveSelectionQuery(
    selection: ODataSelection,
    { applySelection = false }: ResolveSelectionQueryOptions = {}
): ODataQuery {
    const query: ODataQuery = {};

    for (const key of SELECTION_QUERY_KEYS) {
        const value = selection.query[key];
        if (value) {
            query[key] = value;
        }
    }

    if (applySelection) {
        query.$filter = applyIncludesExcludes(query.$filter, selection.includes, selection.excludes, {
            quoted: hasStringIds(selection.entity)
        });
    }

    return query;
}

export function createQueryFromSelection(
    selection: ODataSelection,
    { applySelection = false, extraQuery }: CreateQueryFromSelectionOptions = {}
): string {
    const query = resolveSelectionQuery(selection, { applySelection });

    return stringifyODataQuery(extraQuery ? { ...query, ...extraQuery } : query);
}

function applyIncludesExcludes(
    baseFilter: string | undefined,
    includes: string[] | undefined,
    excludes: string[] | undefined,
    idFilterOptions: { quoted: boolean }
): string | undefined {
    if (includes?.length && excludes?.length) {
        throw new Error("Cannot specify both includes and excludes.");
    }

    const filters: string[] = [];

    if (baseFilter) {
        filters.push(`(${baseFilter})`);
    }

    if (includes?.length) {
        const includesFilter = buildIdInFilter("id", includes, idFilterOptions);
        if (includesFilter) {
            filters.push(includesFilter);
        }
    }

    if (excludes?.length) {
        const excludesFilter = buildIdNotInFilter("id", excludes, idFilterOptions);
        if (excludesFilter) {
            filters.push(excludesFilter);
        }
    }

    return filters.length > 0 ? filters.join(" and ") : undefined;
}
